import { flatten, qsArrayParamsSerializer } from '@higo/api/src/utils/axios';
import {
  ExaminationDetailsDTO,
  mapExaminationDetailsData,
} from '@higo/api/src/models/examinationDetailsDTO';
import {
  AssignedExaminationDTO,
  ExaminationBase,
  ExaminationHistoryDTO,
  ExaminationsAssignmentResultDTO,
  ExaminationsToAssignDTO,
  mapToExaminationBase,
  PaginatedListReqDTO,
  PaginatedListResultDTO,
  UnassignedExaminationDTO,
} from '@higo/api/src/models';
import { ListWrapperDTO, unwrap } from '@higo/api/src/ext/listWrapper';
import {
  ExaminationShareLinkDTO,
  VisitShareLinkDTO,
} from '@higo/api/src/models/examinationShareLinkDTO';
import {
  ExaminationDetailsWithPatientDTO,
  mapToExaminationDetailsWithPatient,
} from '@higo/api/src/models/examinationDetailsWithPatientDTO';
import { NoteDTO } from '@higo/api/src/models/noteDTO';
import { ExaminationDoctorDiagnosisDTO } from '@higo/api/src/models/examinationDoctorDiagnosisDTO';
import { AxiosInstance } from 'axios';
import { map } from 'rambda';

export const fetchUnassignedExaminations =
  (axios: AxiosInstance) =>
  (subPartnersIds?: string, data?: PaginatedListReqDTO) =>
    axios
      .get<PaginatedListResultDTO<UnassignedExaminationDTO>>(
        `api/v2/examinations/unassigned?subPartnersIds=${subPartnersIds}`,
        {
          params: data,
          paramsSerializer: qsArrayParamsSerializer,
        },
      )
      .then(flatten);

export const fetchAssignedExaminations =
  (axios: AxiosInstance) => (data?: PaginatedListReqDTO) =>
    axios
      .get<PaginatedListResultDTO<AssignedExaminationDTO>>(
        `api/v2/examinations/assigned`,
        {
          params: data,
          paramsSerializer: qsArrayParamsSerializer,
        },
      )
      .then(flatten);

export const fetchHistoricalExaminations =
  (axios: AxiosInstance) => (data?: PaginatedListReqDTO) =>
    axios
      .get<PaginatedListResultDTO<ExaminationHistoryDTO>>(
        `api/v2/examinations/history?`,
        {
          params: data,
          paramsSerializer: qsArrayParamsSerializer,
        },
      )
      .then(flatten);

export const fetchBaseExamination = (axios: AxiosInstance) => (ids: number[]) =>
  axios
    .get<ListWrapperDTO<ExaminationBase>>(`/api/v2/examinations/base`, {
      params: { examinationIds: ids },
      paramsSerializer: qsArrayParamsSerializer,
    })
    .then(flatten)
    .then(unwrap)
    .then(map(mapToExaminationBase));

export const fetchBaseVisit = (axios: AxiosInstance) => (ids: number[]) =>
  axios
    .get<ListWrapperDTO<ExaminationBase>>(`/api/visit/base`, {
      params: { visitIds: ids },
      paramsSerializer: qsArrayParamsSerializer,
    })
    .then(flatten)
    .then(unwrap)
    .then(map(mapToExaminationBase));

export const fetchExaminationDetails = (axios: AxiosInstance) => (id: number) =>
  axios
    .get<ExaminationDetailsWithPatientDTO>(`api/v2/examinations/${id}/details`)
    .then(flatten)
    .then(mapToExaminationDetailsWithPatient);

export const deleteDoctorExamination = (axios: AxiosInstance) => (id: number) =>
  axios.delete(`api/v2/examinations/${id}/doctor`);

export const fetchExaminationsSummary =
  (axios: AxiosInstance) => (id: number) =>
    axios
      .get<ExaminationDetailsDTO>(`api/v2/examinations/${id}/summary`)
      .then(flatten)
      .then(mapExaminationDetailsData);

export const fetchVisitSummary = (axios: AxiosInstance) => (id: number) =>
  axios
    .get<ExaminationDetailsDTO>(`/api/visit/${id}/summary`)
    .then(flatten)
    .then(mapExaminationDetailsData);

export const assignExamination = (axios: AxiosInstance) => (id: number) =>
  assignExaminations(axios)({ examinationIds: [id] });

export const assignExaminations =
  (axios: AxiosInstance) => (data: ExaminationsToAssignDTO) =>
    axios
      .post<ExaminationsAssignmentResultDTO>(
        `api/v2/examinations/assignment`,
        data,
      )
      .then(flatten);

export const fetchExaminationNotes =
  (axios: AxiosInstance) => (examinationId: number) =>
    axios
      .get<ListWrapperDTO<NoteDTO>>(`/api/notes/examinations/${examinationId}`)
      .then(flatten)
      .then(unwrap);

export const addExaminationNote =
  (axios: AxiosInstance) => (examinationId: number, noteText: string) =>
    axios.post(`/api/notes/examinations/${examinationId}`, {
      noteText,
    });

// todo: delete the tags after the EP update
export const createShareExaminationLink =
  (axios: AxiosInstance) => (examinationId: number) =>
    axios
      .post<ExaminationShareLinkDTO>(
        `/api/examinations/${examinationId}/share`,
        { tags: [], doctorPortalV2: true },
      )
      .then(flatten);

export const createShareVisitLink =
  (axios: AxiosInstance) => (visitId: number) =>
    axios
      .post<VisitShareLinkDTO>(`/api/visit/${visitId}/share`, {
        tags: [],
        doctorPortalV2: true,
      })
      .then(flatten);

export const saveFinalExaminationDiagnosis =
  (axios: AxiosInstance) =>
  (examinationId: number, dto: ExaminationDoctorDiagnosisDTO) =>
    axios
      .post<ExaminationDoctorDiagnosisDTO>(
        `/api/v2/examinations/${examinationId}/diagnosis`,
        dto,
      )
      .then(flatten);

export const saveDraftExaminationDiagnosis =
  (axios: AxiosInstance) =>
  (examinationId: number, dto: ExaminationDoctorDiagnosisDTO) =>
    axios
      .patch<ExaminationDoctorDiagnosisDTO>(
        `/api/v2/examinations/${examinationId}/diagnosis`,
        dto,
      )
      .then(flatten);

export const saveFinalExaminationDiagnosisForVisits =
  (axios: AxiosInstance) =>
  (visitId: number, dto: ExaminationDoctorDiagnosisDTO) =>
    axios
      .post<ExaminationDoctorDiagnosisDTO>(
        `/api/visit/${visitId}/diagnosis`,
        dto,
      )
      .then(flatten);

export const saveDraftExaminationDiagnosisForVisits =
  (axios: AxiosInstance) =>
  (visitId: number, dto: ExaminationDoctorDiagnosisDTO) =>
    axios
      .patch<ExaminationDoctorDiagnosisDTO>(
        `/api/visit/${visitId}/diagnosis`,
        dto,
      )
      .then(flatten);

export const fetchExaminationDiagnosis =
  (axios: AxiosInstance) => (examinationId: number) =>
    axios
      .get<ExaminationDoctorDiagnosisDTO>(
        `/api/v2/examinations/${examinationId}/diagnosis`,
      )
      .then(flatten);

export const fetchExaminationDiagnosisForVisits =
  (axios: AxiosInstance) => (visitId: number) =>
    axios
      .get<ExaminationDoctorDiagnosisDTO>(`/api/visit/${visitId}/diagnosis`)
      .then(flatten);

export const requestGenerateLeftEarExaminationVideo =
  (axios: AxiosInstance) => (examinationId: number) =>
    axios
      .post<void>(`/api/examinations/${examinationId}/leftEar/video`)
      .then(flatten);

export const requestGenerateRightEarExaminationVideo =
  (axios: AxiosInstance) => (examinationId: number) =>
    axios
      .post<void>(`/api/examinations/${examinationId}/rightEar/video`)
      .then(flatten);

export const requestGenerateSkinExaminationVideo =
  (axios: AxiosInstance) => (examinationId: number) =>
    axios
      .post<void>(`/api/examinations/${examinationId}/skin/video`)
      .then(flatten);

export const requestGenerateThroatExaminationVideo =
  (axios: AxiosInstance) => (examinationId: number) =>
    axios
      .post<void>(`/api/examinations/${examinationId}/throat/video`)
      .then(flatten);

export const fetchSubpartners =
  (axios: AxiosInstance) => (partnerId: number, data?: PaginatedListReqDTO) =>
    axios
      .get<PaginatedListResultDTO<any>>(
        `/api/businessPartners/${partnerId}/subpartners`,
        {
          params: data,
          // paramsSerializer: qsArrayParamsSerializer,
        },
      )
      .then(flatten);

export const generateWebsocketCredentials = (axios: AxiosInstance) => () =>
  axios.post('/api/broker-credentials').then(flatten);

export const getWebsocketCredentials = (axios: AxiosInstance) => () =>
  axios.get('/api/broker-credentials').then(flatten);

export const createVisitForPatient = (axios: AxiosInstance) => (id: number) =>
  axios.post('/api/visit', { patientId: id, visitType: 'LIVE' }).then(flatten);

export const getVisitWithExaminations =
  (axios: AxiosInstance) => (id: number) =>
    axios.get(`/api/visit/${id}`).then(flatten);

export const editOrCloseVisit =
  (axios: AxiosInstance) => (id: number, status: string) =>
    axios.put(`/api/visit/${id}`, { status: status }).then(flatten);

export const getDoctorVisits =
  (axios: AxiosInstance) =>
  (doctorId: number, data?: PaginatedListReqDTO, status?: string | string[]) =>
    axios
      .get(
        status
          ? `/api/visit/doctor/${doctorId}?visitStatus=${status}`
          : `/api/visit/doctor/${doctorId}`,
        {
          params: data,
          paramsSerializer: qsArrayParamsSerializer,
        },
      )
      .then(flatten);

export const getVisitForAllSubpartners =
  (axios: AxiosInstance) =>
  (
    visitStatuses: string[],
    subPartnersIds?: string,
    data?: PaginatedListReqDTO,
    assignedToUserIds?: number | number[] | undefined,
  ) =>
    axios
      .get(
        subPartnersIds !== undefined
          ? `/api/visit?subPartnersIds=${subPartnersIds}`
          : `/api/visit/`,
        {
          params: {
            visitStatuses: visitStatuses,
            assignedToUserIds: assignedToUserIds && assignedToUserIds,
            ...data,
          },
          paramsSerializer: qsArrayParamsSerializer,
        },
      )
      .then(flatten);

export const assignVisits =
  (axios: AxiosInstance) => (data: ExaminationsToAssignDTO) =>
    axios
      .put<ExaminationsAssignmentResultDTO>(
        `api/visit/assignment?visitIds=${data.examinationIds}`,
      )
      .then(flatten);

export const assignVisit = (axios: AxiosInstance) => (id: number[]) =>
  axios.put(`api/visit/assignment?visitIds=${id}`).then(flatten);

export const unassignVisit = (axios: AxiosInstance) => (id: number) =>
  axios.delete(`api/visit/assignment?visitIds=${id}`).then(flatten);
